import React from "react";
import { useParams, Link } from "react-router-dom";
import { PRODUCTS, PRODUCT_LIST } from "@/data/products";
import QuoteForm from "@/components/QuoteForm";
import Seo from "@/components/Seo";
import { useContent } from "@/components/PublicLayout";
import { Check, ArrowRight, Phone } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

export default function Product() {
  const { slug } = useParams();
  const content = useContent();
  const p = PRODUCT_LIST.find((x) => x.slug === slug) || PRODUCTS.auto;
  const phone = content?.phone || "(570) 661-3771";
  const testimonials = (content?.testimonials || []).slice(0, 2);

  return (
    <div>
      <Seo
        title={`${p.type.replace(/ Insurance$/, "")} Insurance Quotes in PA, NJ, NY & FL`}
        description={`${p.sub} Local independent agency in Mount Pocono, PA. Fast, free, no-obligation ${p.type.toLowerCase()} quotes.`}
        path={`/${p.slug}`}
        keywords={`${p.type.toLowerCase()} insurance, cheap ${p.type.toLowerCase()} insurance, ${p.type.toLowerCase()} insurance quote, ${p.type.toLowerCase()} insurance Poconos PA, affordable ${p.type.toLowerCase()} insurance near me`}
        faqs={p.faqs}
      />
      {/* HERO */}
      <section className="relative bg-[#0B1E36] text-white">
        <div className="max-w-7xl mx-auto px-5 py-16 lg:py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] font-bold mb-4">{p.type.replace(/ Insurance$/, "")} Insurance</p>
            <h1 className="font-display text-4xl lg:text-5xl font-black leading-tight">{p.headline}</h1>
            <p className="mt-5 text-lg text-slate-200">{p.sub}</p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              {p.life ? (
                <a href={content?.life_quote_link || "#"} target="_blank" rel="noopener noreferrer" className="bg-[#C5A059] text-[#0B1E36] font-bold px-7 h-14 rounded-sm flex items-center justify-center hover:bg-white transition-colors" data-testid="product-hero-cta">GET AN INSTANT LIFE QUOTE</a>
              ) : (
                <Link to="/quote" state={{ types: [p.type] }} className="bg-[#C5A059] text-[#0B1E36] font-bold px-7 h-14 rounded-sm flex items-center justify-center hover:bg-white transition-colors" data-testid="product-hero-cta">{p.cta}</Link>
              )}
              <a href={`tel:${phone}`} className="border border-white/30 px-7 h-14 rounded-sm flex items-center justify-center gap-2 font-semibold" data-testid="product-hero-call"><Phone size={18} /> {phone}</a>
            </div>
          </div>
          <img src={p.image} alt={p.type} className="rounded-sm w-full h-72 lg:h-96 object-cover" />
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 py-14 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-12">
          <div>
            <p className="text-lg text-slate-700">{p.intro}</p>
          </div>

          <div>
            <h2 className="font-display text-2xl font-bold mb-5">Common coverage options</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {p.coverages.map((c) => (
                <div key={c} className="flex items-center gap-3 bg-white border border-slate-200 rounded-sm px-4 py-3" data-testid={`coverage-${c.replace(/[^a-z]/gi,'-').toLowerCase()}`}>
                  <Check size={18} className="text-[#C5A059] shrink-0" /> <span className="font-medium text-sm">{c}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-display text-2xl font-bold mb-5">Why request a quote from us</h2>
            <ul className="space-y-3">
              {p.benefits.map((b) => <li key={b} className="flex items-start gap-3"><Check size={18} className="text-[#C5A059] mt-1 shrink-0" /> <span>{b}</span></li>)}
            </ul>
          </div>

          {p.discounts?.length > 0 && (
            <div className="bg-[#0B1E36] text-white rounded-sm p-8">
              <h2 className="font-display text-2xl font-bold mb-4">Available discounts</h2>
              <div className="flex flex-wrap gap-3">
                {p.discounts.map((d) => <span key={d} className="border border-[#C5A059] text-[#C5A059] rounded-sm px-4 py-2 text-sm font-semibold">{d}</span>)}
              </div>
              <p className="text-xs text-slate-400 mt-4">Discounts vary by carrier and eligibility. Savings are not guaranteed.</p>
            </div>
          )}

          {p.bundlePromo && (
            <div className="bg-[#C5A059]/15 border border-[#C5A059] rounded-sm p-6">
              <h3 className="font-display text-xl font-bold">Already have auto insurance?</h3>
              <p className="mt-2 text-slate-700">See if bundling could save you money.</p>
              <Link to="/quote" state={{ types: ["Auto + Home Bundle"] }} className="mt-4 inline-flex items-center gap-2 bg-[#0B1E36] text-white font-bold px-5 h-11 rounded-sm" data-testid="bundle-promo-cta">GET MY HOME + AUTO QUOTE <ArrowRight size={16} /></Link>
            </div>
          )}

          {p.disclosure && <p className="text-xs text-slate-500 border-l-2 border-slate-300 pl-4">{p.disclosure}</p>}

          <div>
            <h2 className="font-display text-2xl font-bold mb-5">Frequently asked questions</h2>
            <Accordion type="single" collapsible className="border border-slate-200 rounded-sm bg-white px-4">
              {p.faqs.map((f, i) => (
                <AccordionItem key={i} value={`f${i}`} data-testid={`faq-${i}`}>
                  <AccordionTrigger className="text-left font-semibold">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {testimonials.length > 0 && (
            <div>
              <h2 className="font-display text-2xl font-bold mb-5">What our customers say</h2>
              <div className="grid sm:grid-cols-2 gap-5">
                {testimonials.map((t, i) => (
                  <figure key={i} className="bg-white border border-slate-200 rounded-sm p-5">
                    <blockquote className="italic text-slate-700">“{t.text}”</blockquote>
                    <figcaption className="mt-3 font-semibold text-sm">{t.name}</figcaption>
                  </figure>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* sticky quote form */}
        <div className="lg:sticky lg:top-24 h-fit">
          {p.life ? (
            <div className="bg-white border border-slate-200 rounded-sm p-6 sm:p-8 shadow-xl text-[#0B1E36]" data-testid="life-ethos-card">
              <p className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-bold">Instant Online Quote</p>
              <h3 className="font-display text-2xl font-bold mt-1">Get a Free Life Insurance Quote</h3>
              <p className="text-sm text-slate-600 mt-3">Answer a few quick questions online and get real-time life insurance options. No medical exam required for many applicants.</p>
              <a href={content?.life_quote_link || "#"} target="_blank" rel="noopener noreferrer" className="mt-5 w-full bg-[#C5A059] text-[#0B1E36] font-bold text-base h-14 rounded-sm flex items-center justify-center hover:bg-[#0B1E36] hover:text-white transition-colors" data-testid="life-ethos-btn">START MY LIFE QUOTE</a>
              <p className="text-xs text-slate-500 mt-3">Prefer to talk it through? <a href={`tel:${phone}`} className="font-semibold underline">Call {phone}</a>. Coverage, eligibility, and rates are determined by the carrier and are not guaranteed.</p>
            </div>
          ) : (
            <QuoteForm presetType={p.type} />
          )}
        </div>
      </div>
    </div>
  );
}
